// Authentication state: bootstraps from GET /api/me and exposes the current
// user, household, and members to the app.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "./api";
import type { CurrentUser, Household, Member } from "./meta";

interface MeResponse {
  user: CurrentUser;
  household: Household;
  members: Member[];
}

type Status = "loading" | "authed" | "anon";

interface AuthState {
  status: Status;
  user: CurrentUser | null;
  household: Household | null;
  members: Member[];
  memberById: (id: string) => Member | null;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [data, setData] = useState<MeResponse | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>("/api/me");
      setData(me);
      setStatus("authed");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setData(null);
        setStatus("anon");
      } else {
        // Network/other error: treat as anonymous so the login screen shows.
        setData(null);
        setStatus("anon");
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.post("/api/logout");
    } finally {
      setData(null);
      setStatus("anon");
    }
  }, []);

  const memberById = useCallback(
    (id: string) => data?.members.find((m) => m.id === id) ?? null,
    [data],
  );

  const value: AuthState = {
    status,
    user: data?.user ?? null,
    household: data?.household ?? null,
    members: data?.members ?? [],
    memberById,
    signOut,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
